import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@ekhodealer/ekho-common/rev';

import { useGetServicesProvider } from '@common/api/services-providers/useGetServicesProvider';
import { formatCurrency } from '@common/lib/currency';
import { paths } from '@config/paths';
import { apiClient } from '@config/server/apiClient';
import { productOnboardingEndpoints } from '@config/server/apiEndpoints';

import { useGetOnboardingRecord } from '../api/useGetOnboardingRecord';
import { useGetProductPricing } from '../api/useGetProductPricing';
import {
	hasPreSelectedProductAtom,
	onboardingRecordIdAtom,
	onboardingStepAtom,
	selectedProductIdsAtom,
} from '../atoms/onboardingAtoms';
import { PRODUCT_BY_ID } from '../constants/productCatalog';
import { OnboardingStatus } from '../types';

import { StripePaymentForm } from './StripePaymentForm';

import './OnboardingSteps.scss';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '');

const LOG_PREFIX = '[OnboardingSubscribe]';

/**
 * Step 3 — Add Payment Method
 *
 * Displays the Stripe PaymentElement for the user to enter their payment
 * details. On successful payment, navigates to Step 4 (Processing).
 *
 * Supports two modes:
 *
 * **Path 1 — Net-new dealer (public, no auth):**
 *   - Has `recordId` from the dealer info step
 *   - Calls public `POST /product-onboarding/subscribe` with `onboardingRecordId`
 *
 * **Path 2 — Existing dealer (authenticated):**
 *   - Has `servicesProviderId` from localStorage, no `recordId`
 *   - Reaches here via `?product=ai-bdc` deep-link from frosted lock overlay
 *   - Calls protected `POST /product-onboarding/:spId/billing/subscribe`
 *
 * Mode is auto-detected: if `recordId` is present → Path 1; if `servicesProviderId` is present → Path 2.
 */
export default function OnboardingSubscribeStep() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const setStep = useSetAtom(onboardingStepAtom);
	const [recordId] = useAtom(onboardingRecordIdAtom);
	const [selectedIds, setSelectedIds] = useAtom(selectedProductIdsAtom);
	const setHasPreSelected = useSetAtom(hasPreSelectedProductAtom);
	const hasPreSelected = useAtomValue(hasPreSelectedProductAtom);
	const { svpId } = useParams();
	const servicesProviderId = svpId ?? null;
	const isExistingDealer = !!svpId;

	// Pre-select product from ?product= query param (used by frosted lock overlay in Path 2)
	const preSelectedProductId = searchParams.get('product');
	const hasPreSelectedProduct = useRef(false);
	useEffect(() => {
		if (!preSelectedProductId || hasPreSelectedProduct.current) return;
		if (!PRODUCT_BY_ID.has(preSelectedProductId)) return;
		hasPreSelectedProduct.current = true;
		setSelectedIds(new Set([preSelectedProductId]));
		setHasPreSelected(true);
		console.log(LOG_PREFIX, 'Pre-selected product from query param:', preSelectedProductId);
	}, [preSelectedProductId, setSelectedIds, setHasPreSelected]);

	// Path 1: poll the OnboardingRecord for status changes
	const { data: onboardingRecord } = useGetOnboardingRecord(recordId);

	const { data: livePricing } = useGetProductPricing(svpId ?? undefined);

	// Path 2: fetch SVP to get email for pre-filling payment form
	const { data: svp } = useGetServicesProvider(servicesProviderId ?? '');

	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
	const [isPermanentError, setIsPermanentError] = useState(false);
	const hasCreatedSubscription = useRef(false);

	const totalMonthly = useMemo(() => {
		let total = 0;
		for (const id of selectedIds) {
			const live = livePricing?.[id]?.displayPrice;
			total += live !== undefined ? live : (PRODUCT_BY_ID.get(id)?.priceMonthly ?? 0);
		}
		return total;
	}, [selectedIds, livePricing]);

	const priceLabel =
		totalMonthly > 0 ? `${formatCurrency(totalMonthly / 100, '—', false)}/mo` : undefined;

	// Set step index on mount:
	//   Pre-selected existing dealer → step 0 (Payment is first in PRE_SELECTED_STEPS)
	//   Existing dealer (no pre-select) → step 1 (Payment is second in EXISTING_DEALER_STEPS)
	//   New dealer → step 2 (Payment is third in NEW_DEALER_STEPS)
	useEffect(() => {
		if (isExistingDealer && hasPreSelected) {
			setStep(0);
		} else if (isExistingDealer) {
			setStep(1);
		} else {
			setStep(2);
		}
	}, [setStep, isExistingDealer, hasPreSelected]);

	// Gate: redirect to step 1 if no record AND not an existing dealer
	useEffect(() => {
		if (!recordId && !isExistingDealer) {
			console.log(LOG_PREFIX, 'No record ID and not existing dealer, redirecting to step 1');
			navigate(paths.onboarding.getHref(), { replace: true });
		}
	}, [recordId, isExistingDealer, navigate]);

	// Gate: redirect to products step if no products selected
	// Skip this redirect when a ?product= query param is present (deep-link from frosted lock)
	// because the pre-selection effect above will populate the atom on the same render cycle.
	useEffect(() => {
		if ((recordId || isExistingDealer) && selectedIds.size === 0 && !preSelectedProductId) {
			console.log(LOG_PREFIX, 'No products selected, redirecting to products step');
			if (svpId) {
					navigate(paths.svpSubscribeProducts.getHref(svpId), { replace: true });
				} else {
					navigate(paths.onboardingProducts.getHref(), { replace: true });
				}
		}
	}, [recordId, isExistingDealer, selectedIds, preSelectedProductId, navigate, svpId]);

	// Auto-advance if already subscribed (Path 1 only, e.g. user navigates back)
	useEffect(() => {
		if (onboardingRecord && onboardingRecord.status !== OnboardingStatus.Draft) {
			console.log(
				LOG_PREFIX,
				'Record status is',
				onboardingRecord.status,
				'— already subscribed',
			);
		}
	}, [onboardingRecord]);

	// Handle 3DS redirect return — detect payment_intent_client_secret in URL
	// After 3DS, the payment is already confirmed, so go straight to processing
	useEffect(() => {
		const returnedClientSecret = searchParams.get('payment_intent_client_secret');
		if (returnedClientSecret) {
			console.log(LOG_PREFIX, '3DS return detected, navigating to processing step');
			if (svpId) {
					navigate(paths.svpSubscribeProcessing.getHref(svpId), { replace: true });
				} else {
					navigate(paths.onboardingProcessing.getHref(), { replace: true });
				}
		}
	}, [searchParams, navigate, svpId]);

	// Create subscription once the component is ready
	useEffect(() => {
		if (hasCreatedSubscription.current || clientSecret) {
			return;
		}
		// Need either a recordId (Path 1) or a servicesProviderId (Path 2)
		if (!recordId && !isExistingDealer) {
			return;
		}
		// Wait for products to be selected (pre-selection from ?product= runs in a
		// separate useEffect and the atom update isn't visible until the next render)
		if (selectedIds.size === 0) {
			return;
		}

		// Path 1: skip if already past draft
		if (recordId && onboardingRecord && onboardingRecord.status !== OnboardingStatus.Draft) {
			console.log(LOG_PREFIX, 'Record not in draft status, skipping subscription');
			return;
		}

		hasCreatedSubscription.current = true;
		const productIds = [...selectedIds];

		if (isExistingDealer && servicesProviderId) {
			// Path 2: existing dealer — use protected endpoint
			console.log(
				LOG_PREFIX,
				'Creating subscription for existing dealer',
				servicesProviderId,
				'products:',
				productIds,
			);

			(async () => {
				try {
					const res = await apiClient.post<{
						clientSecret: string;
						subscriptionId: string;
					}>(
						productOnboardingEndpoints.svpSubscribe(servicesProviderId),
						{ productIds },
						{ timeout: 60_000 },
					);
					const data = res.data;
					if (data?.clientSecret) {
						setClientSecret(data.clientSecret);
						console.log(LOG_PREFIX, 'clientSecret SET in state (Path 2)');
					} else {
						console.error(LOG_PREFIX, 'No clientSecret in response (Path 2):', res);
						setSubscriptionError(
							'Subscription created but no client secret returned.',
						);
					}
			} catch (err: unknown) {
				console.error(LOG_PREFIX, 'Subscription creation failed (Path 2):', err);
				const axiosErr = err as {
					response?: { status?: number; data?: { alreadyActiveProductIds?: string[] } };
					message?: string;
				};
				if (axiosErr.response?.status === 409) {
					const alreadyActiveIds = axiosErr.response.data?.alreadyActiveProductIds ?? [];
					const productNames = alreadyActiveIds
						.map((id) => PRODUCT_BY_ID.get(id)?.name ?? id)
						.join(', ');
					setIsPermanentError(true);
					setSubscriptionError(
						alreadyActiveIds.length > 0
							? `You're already subscribed to: ${productNames}.`
							: 'These products are already active on your account.',
					);
				} else {
					const message =
						axiosErr.message || 'Failed to create subscription. Please try again.';
					setSubscriptionError(message);
				}
			}
			})();
		} else if (recordId) {
			// Path 1: net-new dealer — use public endpoint
			console.log(
				LOG_PREFIX,
				'Creating subscription for record',
				recordId,
				'products:',
				productIds,
			);

			(async () => {
				try {
					const res = await apiClient.post<{
						clientSecret: string;
						subscriptionId: string;
					}>(
						productOnboardingEndpoints.subscribe(),
						{
							onboardingRecordId: recordId,
							productIds,
						},
						{ timeout: 60_000 },
					);
					const data = res.data;
					if (data?.clientSecret) {
						setClientSecret(data.clientSecret);
						console.log(LOG_PREFIX, 'clientSecret SET in state (Path 1)');
					} else {
						console.error(LOG_PREFIX, 'No clientSecret in response (Path 1):', res);
						setSubscriptionError(
							'Subscription created but no client secret returned.',
						);
					}
			} catch (err: unknown) {
				console.error(LOG_PREFIX, 'Subscription creation failed (Path 1):', err);
				const message =
					(err as { message?: string })?.message ||
					'Failed to create subscription. Please try again.';
				setSubscriptionError(message);
			}
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs used via .current, deps intentionally omitted
	}, [recordId, isExistingDealer, clientSecret, onboardingRecord, selectedIds]);

	// On successful payment, navigate to the processing step
	const handlePaymentSuccess = useCallback(() => {
		console.log(LOG_PREFIX, 'Payment succeeded, navigating to processing step');
		if (svpId) {
			navigate(paths.svpSubscribeProcessing.getHref(svpId));
		} else {
			navigate(paths.onboardingProcessing.getHref());
		}
	}, [navigate, svpId]);

	const handleRetry = () => {
		hasCreatedSubscription.current = false;
		setSubscriptionError(null);
		setClientSecret(null);
		setIsPermanentError(false);
	};

	console.log(LOG_PREFIX, 'Render state:', {
		recordId,
		servicesProviderId,
		isExistingDealer,
		clientSecret: clientSecret ? `${clientSecret.slice(0, 20)}...` : null,
		subscriptionError,
		selectedIds: [...selectedIds],
	});

	// Don't render if we have neither a record ID nor an existing dealer context
	if (!recordId && !isExistingDealer) {
		return null;
	}

	return (
		<div className='onboarding-step'>
			<div className='onboarding-step__header'>
				<h2>Add your payment method</h2>
				<p>Enter your payment details to activate your selected products.</p>
			</div>

			{clientSecret && (isExistingDealer ? svp !== undefined : onboardingRecord !== undefined) && (
				<Elements stripe={stripePromise} options={{ clientSecret }}>
					<StripePaymentForm
						onSuccess={handlePaymentSuccess}
						priceLabel={priceLabel}
						prefillEmail={onboardingRecord?.email ?? svp?.email}
					/>
				</Elements>
			)}

			{!clientSecret && subscriptionError && (
				<div className='onboarding-step__payment-error'>
					{subscriptionError}
					{!isPermanentError && (
						<Button variant='outline' onPress={handleRetry}>
							Retry
						</Button>
					)}
				</div>
			)}

			{!clientSecret && !subscriptionError && (
				<div className='onboarding-step__payment-skeleton'>
					<div className='onboarding-step__skeleton-row' />
					<div className='onboarding-step__skeleton-row' />
					<div className='onboarding-step__skeleton-row' />
					<div className='onboarding-step__skeleton-button' />
				</div>
			)}

			{/* Hide "Previous" when a product was pre-selected via URL — no need to go back */}
			{!hasPreSelected && (
				<div className='onboarding-step__button-row'>
					<Button
						variant='outline'
						onPress={() => svpId
						? navigate(paths.svpSubscribeProducts.getHref(svpId))
						: navigate(paths.onboardingProducts.getHref())}>
						Previous
					</Button>
				</div>
			)}
		</div>
	);
}
